import React, { memo } from 'react';
import { FormGroup, Label, Row, Col, Input } from 'reactstrap';
import { Controller } from 'react-hook-form';
import { FlexChip } from 'components/FlexChip';
import { Separator } from 'components/Separator';
import { MdAdd, MdClose } from 'react-icons/md';
import ReactButton from 'components/Buttons/ReactButton';
import FlexSelect from 'components/Inputs/FlexSelect';
import PhoneField from './PhoneField';
import { TbMessageCog } from 'react-icons/tb';
import { FaReceipt, FaUserAlt } from 'react-icons/fa';

const MemoizedFlexSelect = memo(FlexSelect);

/**
 * Componente FieldMapping para mapear colunas de arquivos para campos de entidade.
 *
 * Este componente permite aos usuários estabelecer relações entre colunas em um arquivo importado
 * e campos na entidade de destino, com suporte para campos obrigatórios e opcionais.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.fields - Array de objetos de campo a serem mapeados (campos obrigatórios e opcionais já selecionados)
 * @param {Array} props.optionalFields - Array de campos opcionais que podem ser adicionados ao mapeamento
 * @param {Function} props.onRemoveField - Função manipuladora para remover um campo opcional
 * @param {Object|null} props.option - Campo opcional atualmente selecionado para adicionar
 * @param {Function} props.setOption - Função para atualizar o campo opcional selecionado
 */

const FieldMapping = ({
  fields,
  protocolFields = [],
  templateFields = [],
  optionalFields,
  onRemoveField,
  option,
  setOption,
  onAddField,
  useBrazilianDDI,
  handleUseBrazilianDDI,
  control,
  handleFieldChange,
  fileColumns,
  isSubmitting,
  validationErrors,
  showUpdateExistingOption,
  updateExisting,
  setUpdateExisting
}) => {
  const allFields = [...fields, ...optionalFields];
  const contactFields = fields.filter(
    (field) => !field?.isProtocolParams && !field?.isTemplateParams
  );
  const hasPhoneField = allFields.some((field) => field.type === 'TELEPHONE');
  const renderErrorMessage = (fieldName) => {
    if (!validationErrors[fieldName]) return null;

    return (
      <div className="text-danger mt-1">{validationErrors[fieldName]}</div>
    );
  };

  const handleSelectOptionalFields = (selectedOption) => {
    if (selectedOption.isTemplateParams) {
      return {
        icon: <TbMessageCog style={{ margin: '0px 0.5rem  0px 0px' }} />
      };
    } else if (selectedOption.isProtocolParams) {
      return {
        icon: <FaReceipt style={{ margin: '0px 0.5rem  0px 0px' }} />
      };
    }
    return {
      icon: <FaUserAlt style={{ margin: '0px 0.5rem  0px 0px' }} />
    };
  };
  return (
    <>
      <Separator />
      <FlexChip
        text={`Relacione as colunas do arquivo com os campos da entidade. Campos marcados com * são obrigatórios.`}
        className="mb-3"
      />
      {showUpdateExistingOption && (
        <FormGroup check className="mb-3">
          <Label check>
            <Input
              type="checkbox"
              name="hasUpdateContacts"
              checked={updateExisting}
              onChange={(e) => setUpdateExisting(e.target.checked)}
              disabled={isSubmitting}
            />
            Atualizar contatos existentes
          </Label>
        </FormGroup>
      )}

      {hasPhoneField && (
        <FormGroup check className="mb-3">
          <Label check>
            <Input
              type="checkbox"
              name="useBrazilianDDI"
              checked={useBrazilianDDI}
              onChange={handleUseBrazilianDDI}
              disabled={isSubmitting}
            />
            Usar DDI do Brasil (+55) para todos os registros de telefone
          </Label>
        </FormGroup>
      )}

      {contactFields?.map((field) => (
        <FormGroup
          key={field.id}
          className={validationErrors[field.name] ? 'has-error' : ''}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Label className="form-control-label text-capitalize mb-0">
              {field.label}
              {field.isRequired && <span className="text-danger"> *</span>}
            </Label>
            {!field.isRequired && (
              <ReactButton
                type="danger"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  onRemoveField(field.name);
                }}
                disabled={isSubmitting}
              >
                <MdClose />
              </ReactButton>
            )}
          </div>

          <Controller
            control={control}
            name={field.name}
            defaultValue=""
            render={(props) => (
              <>
                {field.type === 'TELEPHONE' ? (
                  <>
                    <PhoneField
                      field={field}
                      value={props.value}
                      handleFieldChange={handleFieldChange}
                      dataOptions={fileColumns}
                      isDisabled={isSubmitting}
                      useBrazilianDDI={useBrazilianDDI}
                      hasError={!!validationErrors[field.name]}
                      closeMenuOnSelect={true}
                    />
                    {renderErrorMessage(field.name)}
                  </>
                ) : (
                  <>
                    <MemoizedFlexSelect
                      isClearable
                      isDisabled={isSubmitting}
                      dataOptions={fileColumns}
                      value={props.value}
                      valueController={handleFieldChange}
                      fieldName={field.name}
                      labelName="col"
                      valueName="col"
                      className={
                        validationErrors[field.name] ? 'is-invalid' : ''
                      }
                      closeMenuOnSelect={true}
                    />
                    {renderErrorMessage(field.name)}
                  </>
                )}
              </>
            )}
          />
        </FormGroup>
      ))}

      {templateFields?.length > 0 && (
        <>
          <div>
            <Separator />
            <h4 className="mb-3">Dados da mensagem de template</h4>
          </div>
          {templateFields.map((field) => (
            <FormGroup
              key={field.id}
              className={validationErrors[field.name] ? 'has-error' : ''}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Label className="form-control-label text-capitalize mb-0">
                  {field.label}
                  {field.isRequired && <span className="text-danger"> *</span>}
                </Label>
                {!field.isRequired && (
                  <ReactButton
                    type="danger"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      onRemoveField(field.name);
                    }}
                    disabled={isSubmitting}
                  >
                    <MdClose />
                  </ReactButton>
                )}
              </div>

              <Controller
                control={control}
                name={field.name}
                defaultValue=""
                render={(props) => (
                  <>
                    {field.type === 'TELEPHONE' ? (
                      <>
                        <PhoneField
                          field={field}
                          value={props.value}
                          handleFieldChange={handleFieldChange}
                          dataOptions={fileColumns}
                          isDisabled={isSubmitting}
                          useBrazilianDDI={useBrazilianDDI}
                          hasError={!!validationErrors[field.name]}
                          closeMenuOnSelect={true}
                        />
                        {renderErrorMessage(field.name)}
                      </>
                    ) : (
                      <>
                        <MemoizedFlexSelect
                          isClearable
                          isDisabled={isSubmitting}
                          dataOptions={fileColumns}
                          value={props.value}
                          valueController={handleFieldChange}
                          fieldName={field.name}
                          labelName="col"
                          valueName="col"
                          className={
                            validationErrors[field.name] ? 'is-invalid' : ''
                          }
                          closeMenuOnSelect={true}
                        />
                        {renderErrorMessage(field.name)}
                      </>
                    )}
                  </>
                )}
              />
            </FormGroup>
          ))}
        </>
      )}

      {protocolFields?.length > 0 && (
        <>
          <div>
            <Separator />
            <h4 className="mb-3">Dados do protocolo</h4>
          </div>
          {protocolFields.map((field) => (
            <FormGroup
              key={field.id}
              className={validationErrors[field.name] ? 'has-error' : ''}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Label className="form-control-label text-capitalize mb-0">
                  {field.label}
                  {field.isRequired && <span className="text-danger"> *</span>}
                </Label>
                {!field.isRequired && (
                  <ReactButton
                    type="danger"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      onRemoveField(field.name);
                    }}
                    disabled={isSubmitting}
                  >
                    <MdClose />
                  </ReactButton>
                )}
              </div>

              <Controller
                control={control}
                name={field.name}
                defaultValue=""
                render={(props) => (
                  <>
                    {field.type === 'TELEPHONE' ? (
                      <>
                        <PhoneField
                          field={field}
                          value={props.value}
                          handleFieldChange={handleFieldChange}
                          dataOptions={fileColumns}
                          isDisabled={isSubmitting}
                          useBrazilianDDI={useBrazilianDDI}
                          hasError={!!validationErrors[field.name]}
                          closeMenuOnSelect={true}
                        />
                        {renderErrorMessage(field.name)}
                      </>
                    ) : (
                      <>
                        <MemoizedFlexSelect
                          isClearable
                          isDisabled={isSubmitting}
                          dataOptions={fileColumns}
                          value={props.value}
                          valueController={handleFieldChange}
                          fieldName={field.name}
                          labelName="col"
                          valueName="col"
                          className={
                            validationErrors[field.name] ? 'is-invalid' : ''
                          }
                          closeMenuOnSelect={true}
                        />
                        {renderErrorMessage(field.name)}
                      </>
                    )}
                  </>
                )}
              />
            </FormGroup>
          ))}
        </>
      )}

      <Separator />
      {optionalFields?.length > 0 && (
        <FormGroup>
          <Label className="mb-2">Adicionar coluna</Label>
          <Row>
            <Col className="pr-2">
              <MemoizedFlexSelect
                isClearable
                isDisabled={isSubmitting || optionalFields.length === 0}
                dataOptions={optionalFields.map(
                  ({
                    label,
                    name,
                    isRequired,
                    isTemplateParams,
                    isProtocolParams
                  }) => ({
                    label: `${label}${isRequired ? '*' : ''}`,
                    value: name,
                    ...handleSelectOptionalFields({
                      isTemplateParams,
                      isProtocolParams
                    })
                  })
                )}
                value={option}
                valueController={(_, value) => setOption(value)}
                fieldName="option"
                labelName="label"
                valueName="value"
              />
            </Col>
            <Col xs="auto">
              <ReactButton
                type="primary"
                onClick={onAddField}
                disabled={!option || isSubmitting}
              >
                <MdAdd size={22} />
              </ReactButton>
            </Col>
          </Row>
        </FormGroup>
      )}
    </>
  );
};

export default memo(FieldMapping);
